import React from 'react';

import { useIntl } from 'ha/i18n';

import { track } from '@ha/analytics';
import { Grid, units, Typography } from '@ha/dsl';
import { makeStyles } from 'tss-react/mui';
import { alpha } from '@mui/material';

import {
  PictureSunBold,
  RealEstateDimensionsBlockBold,
  VideoPlayerSliderBold,
} from '@ha/icons';

interface Props {
  cardLocation?: string | undefined;
  floorPlanCount: number;
  photoCount: number;
  videoCount: number;
}

const useStyles = makeStyles()(theme => ({
  moreMediaIcon: {
    marginRight: units.rem(0.5),
    marginLeft: units.rem(1),
  },
  moreMediaIconFirst: {
    marginLeft: 0,
  },
  moreMediaInfo: {
    marginTop: units.rem(1),
    color: theme.palette.white.main,
  },
  moreMediaOverlay: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: alpha(theme.palette.grey[700], 0.65),
    zIndex: 9,
  },
  moreMediaText: {
    textDecoration: 'underline',
  },
}));

const PhotoGalleryOverlay: React.FC<Props> = ({
  cardLocation,
  floorPlanCount,
  photoCount,
  videoCount,
}) => {
  const { T } = useIntl();
  const { classes, cx } = useStyles();

  const handleOverlayClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      track('more photos available clicked', { cardLocation });
    },
    [cardLocation],
  );

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      direction="column"
      onClick={handleOverlayClick}
      className={classes.moreMediaOverlay}
    >
      <Typography.Paragraph color="white" className={classes.moreMediaText}>
        {T('listing_card.last_slide_media')}
      </Typography.Paragraph>
      <Grid
        container
        className={classes.moreMediaInfo}
        alignItems="center"
        justifyContent="center"
      >
        {photoCount > 0 && (
          <React.Fragment>
            <PictureSunBold
              fontSize="inherit"
              className={cx(classes.moreMediaIcon, classes.moreMediaIconFirst)}
            />
            {photoCount}
          </React.Fragment>
        )}
        {floorPlanCount > 0 && (
          <React.Fragment>
            <RealEstateDimensionsBlockBold
              fontSize="inherit"
              className={classes.moreMediaIcon}
            />
            {floorPlanCount}
          </React.Fragment>
        )}
        {videoCount > 0 && (
          <React.Fragment>
            <VideoPlayerSliderBold
              fontSize="inherit"
              className={classes.moreMediaIcon}
            />
            {videoCount}
          </React.Fragment>
        )}
      </Grid>
    </Grid>
  );
};

export { PhotoGalleryOverlay };
